import { formatPhone } from "../../utils/formatPhone";
import { candidateName } from "../../utils/candidateName";
import { Modal, message } from "antd";
import PageHeading from "../../components/PageHeading";
import { createListSearch } from "../../utils/listSearch";
import { useEffect, useState } from "react";
import { Eye, FilePenLine, RefreshCw, Trash2 } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { CandidateModal } from "./CandidateModal";
import { apiErrorMessage } from "../../utils/apiError";

import { useExpiredDocuments } from "../../context/ExpiredDocumentsContext";

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function DocumentsPerimesPage() {
  const { loading, error, candidates, setCandidates, grouped, dismissDocument, replaceDocument } = useExpiredDocuments();
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [renew, setRenew] = useState(null);
  const [renewFile, setRenewFile] = useState(null);
  const [renewDate, setRenewDate] = useState("");
  const [renewing, setRenewing] = useState(false);
  useEffect(() => () => { if (preview?.url) URL.revokeObjectURL(preview.url); }, [preview]);
  const viewExpiredDocument = async (candidate, document) => {
    if (previewBusy) return;
    setPreviewBusy(document._id);
    try {
      const { data } = await axios.get(`/document/${document._id}`);
      const extension = String(data.name || document.name).split(".").pop().toLowerCase();
      const mime = { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" }[extension];
      if (!mime || typeof data.data !== "string") throw new Error("Format indisponible");
      const raw = atob(data.data.replace(/^data:[^,]+,/, ""));
      const blob = new Blob([Uint8Array.from(raw, char => char.charCodeAt(0))], { type: mime });
      const image = mime.startsWith("image/");
      setPreview({ url: URL.createObjectURL(blob), title: document.type || document.name, image });
    } catch (error) { message.error(apiErrorMessage(error, "Impossible d’afficher ce document périmé.")); }
    finally { setPreviewBusy(null); }
  };
  const openRenew = (candidate, document) => {
    setRenew({ candidate, document });
    setRenewFile(null);
    setRenewDate("");
  };
  const submitRenew = async () => {
    if (!renew?.document || renewing) return;
    if (!renewFile && !renewDate) {
      message.warning("Ajoutez un nouveau document ou une nouvelle date d’expiration.");
      return;
    }
    setRenewing(true);
    try {
      const payload = {};
      if (renewDate) {
        const date = new Date(`${renewDate}T12:00:00`);
        payload.expirationDate = date.toISOString();
      }
      if (renewFile) {
        payload.data = await fileToBase64(renewFile);
        payload.name = renewFile.name;
      }
      await axios.patch(`document/${renew.document._id}`, payload);
      replaceDocument?.(renew.document._id, { name: payload.name || renew.document.name, expirationDate: payload.expirationDate || renew.document.expirationDate });
      message.success("Document renouvelé.");
      setRenew(null);
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible de renouveler ce document."));
    } finally {
      setRenewing(false);
    }
  };
  const deleteDocument = (document) => {
    Modal.confirm({
      title: "Supprimer ce document ?",
      content: `Le document « ${document.type || document.name || "document"} » sera supprimé.`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      zIndex: 2000,
      onOk: () => axios.delete(`document/${document._id}`).then(() => {
        dismissDocument?.(document._id);
        message.success("Document supprimé.");
      }).catch((error) => message.error(apiErrorMessage(error, "Impossible de supprimer ce document."))),
    });
  };
  const [selected, setSelected] = useState(null);
  const visible = grouped.filter(createListSearch(search, ["candidate.firstname", "candidate.lastname", "candidate.phone", "candidate.email", "candidate.town", "documents.type", "documents.name", "documents.expirationDate"]));
  return <div className="">
    <PageHeading icon="documents" title={<> Documents périmés </>} description={<> Documents à renouveler des candidats en poste aujourd’hui ou ayant travaillé dans les 60 derniers jours. </>} />
    <input aria-label="Rechercher un candidat ou un document périmé" placeholder="Candidat, type de document…" value={search} onChange={e => setSearch(e.target.value)} className="my-5 w-full max-w-md rounded-lg border border-slate-200 px-3 py-2.5" />
    {error && <p role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">{error}</p>}
    {loading ? <p role="status">Vérification des documents en cours…</p> : <div className="app-table-container"><table className="app-table"><thead><tr>{["Candidat", "Documents à renouveler", "Action"].map(label => <th key={label}>{label}</th>)}</tr></thead><tbody>
      {visible.map(({candidate, documents}) => <tr key={candidate._id}>
        <td><strong>{candidateName(candidate)}</strong><p className="mt-1 text-xs text-slate-500">{formatPhone(candidate.phone)}</p><p className="mt-2 text-xs font-medium text-red-700">{documents.length} document{documents.length > 1 ? "s" : ""} périmé{documents.length > 1 ? "s" : ""}</p></td>
        <td><ul className="space-y-4">{documents.map((document, index) => <li key={document._id || index} className="grid gap-2">
          <div><span className="font-medium">{document.type || document.name}</span>{document.name && document.name !== document.type && <p className="mt-1 text-xs text-slate-500">{document.name}</p>}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-xs font-medium text-red-700">Expiré le {new Date(`${String(document.expirationDate).slice(0,10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
            <button type="button" disabled={Boolean(previewBusy)} onClick={() => viewExpiredDocument(candidate, document)} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline disabled:opacity-50" aria-label={`Voir le document périmé : ${document.type || document.name} de ${candidate.firstname} ${candidate.lastname}`}><Eye size={15} className="shrink-0" />{previewBusy === document._id ? "Chargement…" : "Voir"}</button>
            <button type="button" onClick={() => openRenew(candidate, document)} className="inline-flex items-center gap-1 text-xs text-orange-700 hover:underline"><RefreshCw size={15} className="shrink-0" />Renouveler</button>
            <button type="button" onClick={() => setSelected(candidate)} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"><FilePenLine size={15} className="shrink-0" />Relancer</button>
            <button type="button" onClick={() => deleteDocument(document)} className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline"><Trash2 size={15} className="shrink-0" />Supprimer</button>
          </div>
        </li>)}</ul></td>
        <td className="text-center"><button onClick={() => setSelected(candidate)} className="inline-flex items-center gap-2 text-blue-700 hover:underline"><FilePenLine size={16} />Mettre à jour les documents</button></td>
      </tr>)}
      {!visible.length && <tr><td colSpan={3} className="app-table-message">{error ? "Aucun document périmé trouvé dans les dossiers vérifiés." : "Aucun document périmé pour ces critères."}</td></tr>}
    </tbody></table></div>}
    <Modal open={Boolean(renew)} title="Renouveler le document" onCancel={() => !renewing && setRenew(null)} footer={null} maskClosable={!renewing} closable={!renewing}>
      {renew && <form className="space-y-4 pt-2" onSubmit={(event) => { event.preventDefault(); submitRenew(); }}>
        <p className="text-sm text-slate-600">{candidateName(renew.candidate)} — {renew.document.type || renew.document.name}</p>
        <label className="block text-sm">Nouveau fichier (facultatif)<input type="file" accept=".pdf,image/*" onChange={(event) => setRenewFile(event.target.files?.[0] || null)} className="mt-1 block w-full text-sm" /></label>
        <label className="block text-sm">Nouvelle date d’expiration<input type="date" value={renewDate} onChange={(event) => setRenewDate(event.target.value)} className="mt-1 w-full rounded-lg border p-2" /></label>
        <div className="flex justify-end gap-3"><button type="button" disabled={renewing} onClick={() => setRenew(null)} className="rounded-lg border px-3 py-2 text-sm">Annuler</button><button disabled={renewing} className="rounded-lg bg-orange-600 px-3 py-2 text-sm font-semibold text-white">{renewing ? "Enregistrement…" : "Renouveler"}</button></div>
      </form>}
    </Modal>
    <Modal open={Boolean(preview)} title={preview?.title} onCancel={() => setPreview(null)} footer={null} width={900}>
      {preview && (preview.image ? <img src={preview.url} alt={preview.title} className="mx-auto max-h-[70vh] max-w-full object-contain" /> : <iframe title="Document périmé" src={preview.url} className="h-[70vh] w-full border-0" />)}
    </Modal>
    {selected && <CandidateModal key={selected._id} initialTab="documents" isOpen onClose={() => setSelected(null)} candidate={selected} candidates={candidates} setCandidates={setCandidates} />}
  </div>;
}
